import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SUZA from '../../assets/SUZA.png';
import axiosInstance from '../../api/axiosInstance';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FiUser, FiMail, FiLock, FiEye, FiEyeOff, FiAtSign } from 'react-icons/fi';

const StudentRegistration = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const passwordStrength = (pwd) => {
    if (!pwd) return null;
    if (pwd.length < 6) return { label: 'Too short', color: '#dc3545', width: '25%' };
    if (pwd.length < 8) return { label: 'Weak', color: '#fd7e14', width: '50%' };
    if (/[A-Z]/.test(pwd) && /[0-9]/.test(pwd)) return { label: 'Strong', color: '#198754', width: '100%' };
    return { label: 'Fair', color: '#ffc107', width: '75%' };
  };

  const strength = passwordStrength(formData.password);

  const handleRegister = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match.');
      return;
    }
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters.');
      return;
    }

    setLoading(true);
    try {
      const submitData = new FormData();
      submitData.append('firstName', formData.firstName);
      submitData.append('lastName', formData.lastName);
      submitData.append('username', formData.username);
      submitData.append('email', formData.email);
      submitData.append('password', formData.password);

      const response = await axiosInstance.post('/api/application', submitData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.status === 201) {
        toast.success('Account created! Redirecting to login...');
        setTimeout(() => navigate('/'), 2000);
      }
    } catch (error) {
      const msg = error.response?.data;
      if (typeof msg === 'string' && msg.toLowerCase().includes('username')) {
        toast.error('Username already taken. Please choose another.');
      } else if (typeof msg === 'string' && msg.toLowerCase().includes('email')) {
        toast.error('An account with this email already exists.');
      } else {
        toast.error(typeof msg === 'string' ? msg : 'Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const InputGroup = ({ icon: Icon, children }) => (
    <div className="input-group">
      <span className="input-group-text bg-light border-end-0">
        <Icon className="text-muted" />
      </span>
      {children}
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', display: 'flex' }}>
      {/* Left form panel */}
      <div
        className="d-flex flex-column align-items-center justify-content-center flex-grow-1 p-4"
        style={{ background: '#f8fafc' }}
      >
        {/* Mobile logo */}
        <div className="d-lg-none text-center mb-4">
          <img src={SUZA} alt="SUZA Logo" style={{ height: 70 }} />
          <h5 className="fw-bold mt-2 text-dark">Swahili For Foreigners System</h5>
        </div>

        <div className="bg-white rounded-4 shadow-sm p-4 p-md-5" style={{ width: '100%', maxWidth: 480 }}>
          <h4 className="fw-bold text-dark mb-1">Create your account</h4>
          <p className="text-muted small mb-4">Join the Swahili For Foreigners program</p>

          <form onSubmit={handleRegister}>
            {/* Name row */}
            <div className="row g-3 mb-3">
              <div className="col-6">
                <label className="form-label small fw-semibold text-muted">First Name</label>
                <InputGroup icon={FiUser}>
                  <input
                    type="text"
                    className="form-control border-start-0 bg-light"
                    name="firstName"
                    placeholder="John"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    style={{ boxShadow: 'none' }}
                  />
                </InputGroup>
              </div>
              <div className="col-6">
                <label className="form-label small fw-semibold text-muted">Last Name</label>
                <InputGroup icon={FiUser}>
                  <input
                    type="text"
                    className="form-control border-start-0 bg-light"
                    name="lastName"
                    placeholder="Doe"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    style={{ boxShadow: 'none' }}
                  />
                </InputGroup>
              </div>
            </div>

            {/* Username */}
            <div className="mb-3">
              <label className="form-label small fw-semibold text-muted">Username</label>
              <InputGroup icon={FiAtSign}>
                <input
                  type="text"
                  className="form-control border-start-0 bg-light"
                  name="username"
                  placeholder="Choose a username"
                  value={formData.username}
                  onChange={handleInputChange}
                  required
                  style={{ boxShadow: 'none' }}
                />
              </InputGroup>
            </div>

            {/* Email */}
            <div className="mb-3">
              <label className="form-label small fw-semibold text-muted">Email Address</label>
              <InputGroup icon={FiMail}>
                <input
                  type="email"
                  className="form-control border-start-0 bg-light"
                  name="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  style={{ boxShadow: 'none' }}
                />
              </InputGroup>
            </div>

            {/* Password */}
            <div className="mb-1">
              <label className="form-label small fw-semibold text-muted">Password</label>
              <div className="input-group">
                <span className="input-group-text bg-light border-end-0">
                  <FiLock className="text-muted" />
                </span>
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="form-control border-start-0 border-end-0 bg-light"
                  name="password"
                  placeholder="Min. 6 characters"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  style={{ boxShadow: 'none' }}
                />
                <button
                  type="button"
                  className="input-group-text bg-light border-start-0"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FiEyeOff className="text-muted" /> : <FiEye className="text-muted" />}
                </button>
              </div>
            </div>

            {/* Password strength bar */}
            {strength && (
              <div className="mb-3 mt-1">
                <div className="d-flex justify-content-between mb-1">
                  <span className="small text-muted">Strength</span>
                  <span className="small fw-semibold" style={{ color: strength.color }}>{strength.label}</span>
                </div>
                <div className="progress" style={{ height: 4 }}>
                  <div
                    className="progress-bar"
                    style={{ width: strength.width, background: strength.color, transition: 'width 0.3s' }}
                  />
                </div>
              </div>
            )}

            {/* Confirm password */}
            <div className="mb-4">
              <label className="form-label small fw-semibold text-muted">Confirm Password</label>
              <div className="input-group">
                <span className="input-group-text bg-light border-end-0">
                  <FiLock className="text-muted" />
                </span>
                <input
                  type={showConfirm ? 'text' : 'password'}
                  className="form-control border-start-0 border-end-0 bg-light"
                  name="confirmPassword"
                  placeholder="Re-enter password"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  required
                  style={{ boxShadow: 'none' }}
                />
                <button
                  type="button"
                  className="input-group-text bg-light border-start-0"
                  onClick={() => setShowConfirm(!showConfirm)}
                >
                  {showConfirm ? <FiEyeOff className="text-muted" /> : <FiEye className="text-muted" />}
                </button>
              </div>
              {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                <div className="small text-danger mt-1">Passwords don't match</div>
              )}
            </div>

            {/* Submit */}
            <button
              type="submit"
              className="btn w-100 py-2 fw-semibold text-white"
              disabled={loading}
              style={{
                background: 'linear-gradient(135deg, #0d6efd, #00c9a7)',
                border: 'none',
                borderRadius: 8,
                fontSize: '1rem',
              }}
            >
              {loading ? (
                <span>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  Creating account...
                </span>
              ) : 'Create Account'}
            </button>
          </form>

          <div className="text-center mt-4">
            <span className="text-muted small">Already have an account? </span>
            <Link to="/" className="small fw-semibold text-decoration-none" style={{ color: '#0d6efd' }}>
              Sign In
            </Link>
          </div>
        </div>
      </div>

      {/* Right branding panel */}
      <div
        className="d-none d-lg-flex flex-column align-items-center justify-content-center text-white p-5"
        style={{
          width: '40%',
          background: 'linear-gradient(160deg, #00c9a7 0%, #0099cc 50%, #0d6efd 100%)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div style={{
          position: 'absolute', width: 280, height: 280, borderRadius: '50%',
          background: 'rgba(255,255,255,0.07)', top: -60, right: -80,
        }} />
        <div style={{
          position: 'absolute', width: 180, height: 180, borderRadius: '50%',
          background: 'rgba(255,255,255,0.07)', bottom: 60, left: -40,
        }} />

        <img src={SUZA} alt="SUZA Logo" style={{ height: 100, marginBottom: 32, filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.2))' }} />
        <h2 className="fw-bold text-center mb-3" style={{ fontSize: '1.8rem', lineHeight: 1.3 }}>
          Start Your<br />Swahili Journey
        </h2>
        <p className="text-center opacity-75" style={{ maxWidth: 260, fontSize: '0.95rem' }}>
          Register today and get access to courses, resources, and expert instructors.
        </p>

        <div className="mt-5 d-flex flex-column gap-2" style={{ maxWidth: 260 }}>
          {[
            '✓ Access to all course materials',
            '✓ Expert Swahili instructors',
            '✓ Field trips & cultural activities',
          ].map((item) => (
            <div key={item} className="small opacity-90">{item}</div>
          ))}
        </div>
      </div>

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default StudentRegistration;
